import { BadgeStatus } from '../badge-status';
import { EndStatus } from '../fetch-status/end-status';
import { SearchNothing } from '../fetch-status/search-nothing';
import { TextTooltip } from '../text-tooltip';
import { Skeleton } from '../ui/skeleton';
import { UploadedImage } from '../uploaded-image';
import { ViewUserIcon } from '@/client/assets';
import { IS_DEBUG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import useSearchStore from '@/client/store/search-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useSearchMarket } from '@/libs/react-sdk/api/use-search-market';
import { useSearchAll } from '@/libs/react-sdk/api/use-search-all';
import { useSearchUser } from '@/libs/react-sdk/api/use-search-user';
import { SearchMarket, SearchUser } from '@/libs/sdk/api';
import useDebounce from 'ahooks/lib/useDebounce';
import useInViewport from 'ahooks/lib/useInViewport';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

export const SearchUsersSkeleton = ({ len = 1 }: { len?: number }) => {
  return Array.from({ length: len }).map((_, index) => <SearchUserItemSkeleton key={`MarketItemSkeleton-${index}`} />);
};

export const SearchUserItemSkeleton = memo(() => {
  return (
    <div className="p-2 gap-3 flex items-center min-h-12 w-full">
      <Skeleton className="size-8 flex-shrink-0 rounded-2" />
      <Skeleton className="w-full h-6" />
    </div>
  );
});
SearchUserItemSkeleton.displayName = 'SearchUserItemSkeleton';

type UserItemProps = {
  user: SearchUser;
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  onClick?: () => void;
};
export const UserItem = ({ user, className, root, onViewChange, onClick }: UserItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useInViewport(ref, {
    root,
    callback: entry => {
      onViewChange?.(entry.isIntersecting);
    },
  });
  return (
    <div
      ref={ref}
      onClick={onClick}
      className={cn(
        'p-2 gap-3 flex items-center justify-between w-full min-h-12 hover:bg-foreground/5 rounded-md cursor-pointer',
        className
      )}
    >
      <UploadedImage fileKey={user.avatar} className="size-8 rounded-2" />
      <TextTooltip tooltip={user.name} className="flex-1 line-clamp-1">
        {user.name}
      </TextTooltip>
    </div>
  );
};

export const Users = ({ keyword }: { keyword: string }) => {
  const { t } = useTranslation();
  const pageSize = 4;
  const [, navigate] = useLocation();
  const setSearchOpen = useModelStore(state => state.setSearchOpen);
  const addSearch = useSearchStore(state => state.addSearch);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useSearchUser(api, {
    baseTokenAddress,
    keyword,
    page: 1,
    pageSize,
  });
  const userTotal = useMemo(() => data?.pages[0]?.userTotal ?? 0, [data?.pages]);
  const users = useMemo(() => data?.pages.flatMap(item => item.users) ?? [], [data?.pages]);
  const moreLastIndex = useMemo(() => users.length - 3, [users.length]);
  const sellAll = useMemo(() => users.length > pageSize, [pageSize, users.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (sellAll && isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more user search', keyword, moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div className="space-y-2">
      <h3 className="text-3 leading-3 text-foreground/70 ps-2">{t('users')}</h3>
      <div className="w-full">
        {users?.map((item, index) => (
          <UserItem
            key={`search-user-${item.address}`}
            user={item}
            onClick={() => {
              addSearch(keyword);
              setSearchOpen(false);
              navigate(`/user/${item.uid}`);
            }}
            onViewChange={isInView => loadMoreItems(isInView, index)}
          />
        ))}
        {hasNextPage && !sellAll && (
          <button type="button" className="text-3 text-foreground/70" disabled={isFetching} onClick={() => fetchNextPage()}>
            {t('sell-all-total-results', { total: userTotal })}
          </button>
        )}
        {isFetching && <SearchUsersSkeleton len={pageSize} />}
        {!isFetching && !hasNextPage && users.length > pageSize && <EndStatus />}
      </div>
    </div>
  );
};

export const SearchMarketsSkeleton = ({ len = 1 }: { len?: number }) => {
  return Array.from({ length: len }).map((_, index) => <SearchMarketItemSkeleton key={`MarketItemSkeleton-${index}`} />);
};

export const SearchMarketItemSkeleton = memo(() => {
  return (
    <div className="p-2 gap-3 flex items-center min-h-12 w-full">
      <Skeleton className="size-8 flex-shrink-0 rounded-2" />
      <Skeleton className="w-full h-6" />
      <Skeleton className="w-12 h-6" />
    </div>
  );
});
SearchMarketItemSkeleton.displayName = 'SearchMarketItemSkeleton';

type SearchMarketItemProps = {
  market: SearchMarket;
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  onClick?: () => void;
};
export const SearchMarketItem = ({ market, className, root, onViewChange, onClick }: SearchMarketItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useInViewport(ref, {
    root,
    callback: entry => {
      onViewChange?.(entry.isIntersecting);
    },
  });
  return (
    <div
      ref={ref}
      onClick={onClick}
      className={cn(
        'p-2 gap-3 flex w-full items-center justify-between hover:bg-foreground/5 rounded-md min-h-12 cursor-pointer',
        className
      )}
    >
      <UploadedImage fileKey={market.picUrl} className="size-8 rounded-2" />
      <TextTooltip tooltip={market.name} className="flex-1 line-clamp-1">
        {market.name}
      </TextTooltip>
      <BadgeStatus deadline={market.deadline} status={market.status} />
      <div className="gap-1 flex items-center text-3 flex-shrink-0">
        <ViewUserIcon className="size-3 fill-foreground/70" />
        <span className="text-foreground/70">{market.participantsCount}</span>
      </div>
    </div>
  );
};

export const Markets = ({ keyword }: { keyword: string }) => {
  const { t } = useTranslation();
  const pageSize = 8;
  const [, navigate] = useLocation();
  const setSearchOpen = useModelStore(state => state.setSearchOpen);
  const addSearch = useSearchStore(state => state.addSearch);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useSearchMarket(api, {
    baseTokenAddress,
    keyword,
    page: 1,
    pageSize,
  });
  const marketTotal = useMemo(() => data?.pages[0]?.marketTotal ?? 0, [data?.pages]);
  const markets = useMemo(() => data?.pages.flatMap(item => item.markets) ?? [], [data?.pages]);
  const moreLastIndex = useMemo(() => markets.length - 3, [markets.length]);
  const sellAll = useMemo(() => markets.length > pageSize, [markets.length, pageSize]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (sellAll && isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more market search', keyword, moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div className="space-y-2">
      <h3 className="text-3 leading-3 text-foreground/70 ps-2">{t('markets')}</h3>
      <div className="w-full">
      {markets?.map((item, index) => (
        <SearchMarketItem
          key={`search-market-${item.address}`}
          market={item}
          onClick={() => {
            addSearch(keyword);
            setSearchOpen(false);
            navigate(`/market/${item.address}`);
          }}
          onViewChange={isInView => loadMoreItems(isInView, index)}
        />
      ))}
      {hasNextPage && !sellAll && (
        <button type="button" className="text-3 text-foreground/70" disabled={isFetching} onClick={() => fetchNextPage()}>
          {t('sell-all-total-results', { total: marketTotal })}
        </button>
      )}
      {isFetching && <SearchMarketsSkeleton len={pageSize} />}
      {!isFetching && !hasNextPage && markets.length > pageSize && <EndStatus />}
      </div>
    </div>
  );
};

type SearchResultProps = {
  className?: string;
  keyword: string;
};
export const SearchResult = ({ keyword }: SearchResultProps) => {
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const debounceKeyword = useDebounce(keyword, { wait: 500 });
  const searchAll = useSearchAll(api, { baseTokenAddress, keyword: debounceKeyword, page: 1, pageSize: 4 });
  const noResult = useMemo(
    () => !searchAll.isFetching && searchAll.data?.pages?.[0]?.marketTotal === 0 && searchAll.data?.pages?.[0]?.userTotal === 0,
    [searchAll.isFetching, searchAll.data?.pages]
  );
  const hasUsers = useMemo(() => (searchAll.data?.pages?.[0]?.userTotal ?? 0) > 0, [searchAll.data?.pages]);
  const hasMarkets = useMemo(() => (searchAll.data?.pages?.[0]?.marketTotal ?? 0) > 0, [searchAll.data?.pages]);
  if (noResult) return <SearchNothing text={keyword} />;
  return (
    <div className="space-y-4">
      {hasUsers && <Users keyword={keyword} />}
      {hasMarkets && <Markets keyword={keyword} />}
    </div>
  );
};

export default SearchResult;
